import {
  ColumnMapping,
  convertExcelDate,
  ExcelProcessorConfig,
  HeaderConfig,
  ParsedExcelData,
  parseNumber,
  ValidationError
} from '@/lib/excel';
import { exportFromApi, fetchAllForExport, type FileDef, save } from '@/lib/excel/export';
import type { SearchValues } from '@/components/custom/erp';
import { toPartialUpdateBody } from './import-transform';
import type { VatTuSanPham } from '@/types/schemas';
import type { ImportBody } from './types';
import { QUERY_KEYS } from '@/constants';

export const HEADERS = {
  UUID: 'UUID',
  MA_SAN_PHAM: 'Mã sản phẩm*',
  TEN_SAN_PHAM: 'Tên sản phẩm*',
  TEN_KHAC: 'Tên khác',
  DON_VI_TINH: 'Đơn vị tính*',
  THEO_DOI_TON_KHO: 'Theo dõi tồn kho',
  THEO_DOI_LO: 'Theo dõi lô',
  THEO_DOI_TON_QUY_CACH: 'Theo dõi tồn quy cách',
  LOAI_VAT_TU: 'Loại vật tư*',
  NHOM_1: 'Nhóm 1',
  NHOM_2: 'Nhóm 2',
  NHOM_3: 'Nhóm 3',
  MA_KHO_MAC_DINH: 'Mã kho mặc định',
  VI_TRI_MAC_DINH: 'Vị trí mặc định',
  MA_THUE_MAC_DINH: 'Mã thuế mặc định',
  MA_THUE_NHAP_KHAU: 'Mã thuế nhập khẩu',
  TK_KHO_CHI_PHI: 'Tk kho/ chi phí',
  SUA_TK_VAT_TU: 'Sửa tk vật tư',
  TK_DOANH_THU: 'Tk doanh thu',
  TK_GIA_VON: 'Tk giá vốn',
  TK_CHIET_KHAU: 'Tk chiết khấu',
  TK_KHUYEN_MAI: 'Tk khuyến mãi',
  TK_TRA_LAI: 'Tk trả lại',
  TK_SAN_PHAM_DO_DANG: 'Tk s/p dở dang',
  TK_CHI_PHI_NVL: 'Tk chi phí NVL',
  MA_PHU: 'Mã phụ',
  MA_VACH: 'Mã vạch (QRCode)',
  THE_TICH: 'Thể tích',
  KHOI_LUONG: 'Khối lượng',
  NUOC_SAN_XUAT: 'Nước sản xuất',
  MAU_SAC: 'Màu sắc',
  KICH_CO: 'Kích cỡ',
  MA_BO_PHAN: 'Mã bộ phận',
  MA_PHI: 'Mã phí',
  TON_TOI_THIEU: 'Tồn tối thiểu',
  TON_TOI_DA: 'Tồn tối đa',
  NGAY_NHAP_CUOI: 'Ngày nhập cuối',
  NGAY_XUAT_CUOI: 'Ngày xuất cuối',
  GHI_CHU: 'Ghi chú',
  TRANG_THAI: 'Trạng thái'
} as const;

const HEADER_CONFIG: HeaderConfig[] = [
  { header: HEADERS.UUID, key: 'uuid', type: 'main' },
  { header: HEADERS.MA_SAN_PHAM, key: 'ma_vt', type: 'main' },
  { header: HEADERS.TEN_SAN_PHAM, key: 'ten_vt', type: 'main' },
  { header: HEADERS.TEN_KHAC, key: 'ten_vt2', type: 'main' },
  { header: HEADERS.DON_VI_TINH, key: 'dvt', type: 'main' },
  { header: HEADERS.THEO_DOI_TON_KHO, key: 'ton_kho_yn', type: 'main' },
  { header: HEADERS.THEO_DOI_LO, key: 'lo_yn', type: 'main' },
  { header: HEADERS.THEO_DOI_TON_QUY_CACH, key: 'qc_yn', type: 'main' },
  { header: HEADERS.LOAI_VAT_TU, key: 'ma_lvt', type: 'main' },
  { header: HEADERS.NHOM_1, key: 'nh_vt1', type: 'main' },
  { header: HEADERS.NHOM_2, key: 'nh_vt2', type: 'main' },
  { header: HEADERS.NHOM_3, key: 'nh_vt3', type: 'main' },
  { header: HEADERS.MA_KHO_MAC_DINH, key: 'ma_kho', type: 'main' },
  { header: HEADERS.VI_TRI_MAC_DINH, key: 'ma_vi_tri', type: 'main' },
  { header: HEADERS.MA_THUE_MAC_DINH, key: 'ma_thue', type: 'main' },
  { header: HEADERS.MA_THUE_NHAP_KHAU, key: 'ma_thue_nk', type: 'main' },
  { header: HEADERS.TK_KHO_CHI_PHI, key: 'tk_khcp', type: 'main' },
  { header: HEADERS.SUA_TK_VAT_TU, key: 'sua_tk_vt', type: 'main' },
  { header: HEADERS.TK_DOANH_THU, key: 'tk_dt', type: 'main' },
  { header: HEADERS.TK_GIA_VON, key: 'tk_gv', type: 'main' },
  { header: HEADERS.TK_CHIET_KHAU, key: 'tk_ck', type: 'main' },
  { header: HEADERS.TK_KHUYEN_MAI, key: 'tk_km', type: 'main' },
  { header: HEADERS.TK_TRA_LAI, key: 'tk_tl', type: 'main' },
  { header: HEADERS.TK_SAN_PHAM_DO_DANG, key: 'tk_dd', type: 'main' },
  { header: HEADERS.TK_CHI_PHI_NVL, key: 'tk_cp_nvl', type: 'main' },
  { header: HEADERS.MA_PHU, key: 'ma_vt2', type: 'main' },
  { header: HEADERS.MA_VACH, key: 'ma_barcode', type: 'main' },
  { header: HEADERS.THE_TICH, key: 'the_tich', type: 'main' },
  { header: HEADERS.KHOI_LUONG, key: 'khoi_luong', type: 'main' },
  { header: HEADERS.NUOC_SAN_XUAT, key: 'nuoc_sx', type: 'main' },
  { header: HEADERS.MAU_SAC, key: 'ma_mau', type: 'main' },
  { header: HEADERS.KICH_CO, key: 'ma_kich_co', type: 'main' },
  { header: HEADERS.MA_BO_PHAN, key: 'ma_bp', type: 'main' },
  { header: HEADERS.MA_PHI, key: 'ma_phi', type: 'main' },
  { header: HEADERS.TON_TOI_THIEU, key: 'sl_min', type: 'main' },
  { header: HEADERS.TON_TOI_DA, key: 'sl_max', type: 'main' },
  { header: HEADERS.NGAY_NHAP_CUOI, key: 'ngay_nhap', type: 'main' },
  { header: HEADERS.NGAY_XUAT_CUOI, key: 'ngay_xuat', type: 'main' },
  { header: HEADERS.GHI_CHU, key: 'ghi_chu', type: 'main' },
  { header: HEADERS.TRANG_THAI, key: 'status', type: 'main' }
];

const boolToExcel = (value: unknown) => {
  if (value === true || value === 1 || value === '1') return 1;
  if (value === false || value === 0 || value === '0') return 0;
  return '';
};

const CHECKBOX_NUMBER_FORMAT = '0';
const DECIMAL_NUMBER_FORMAT = '#,##0.00';
const toExcelText = (value: unknown) => (value === null || value === undefined ? '' : String(value));

const getCode = (row: VatTuSanPham, field: string, dataField: string, codeField: string) => {
  const record = row as unknown as Record<string, unknown>;
  const related = record[dataField] as Record<string, unknown> | null | undefined;
  return toExcelText(related?.[codeField] || record[field]);
};

const getName = (row: VatTuSanPham, field: string, dataField: string, nameField: string) => {
  const record = row as unknown as Record<string, unknown>;
  const related = record[dataField] as Record<string, unknown> | null | undefined;
  return toExcelText(related?.[nameField] || related?.name || related?.ten || record[field]);
};

const toExcelNumber = (value: unknown) => {
  if (value === null || value === undefined || value === '') return '';

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : '';
  }

  const rawValue = String(value).trim().replace(/\s/g, '');
  const lastCommaIndex = rawValue.lastIndexOf(',');
  const lastDotIndex = rawValue.lastIndexOf('.');
  const decimalSeparator = lastCommaIndex > -1 || lastDotIndex > -1 ? (lastCommaIndex > lastDotIndex ? ',' : '.') : '';
  const normalizedValue = decimalSeparator
    ? rawValue.replace(new RegExp(`\\${decimalSeparator === ',' ? '.' : ','}`, 'g'), '').replace(decimalSeparator, '.')
    : rawValue;
  const numericValue = Number(normalizedValue);
  return Number.isFinite(numericValue) ? numericValue : '';
};

const VAT_TU_SAN_PHAM_EXPORT_FILE: FileDef<VatTuSanPham> = {
  title: 'Danh mục vật tư, sản phẩm',
  name: 'Danh mục vật tư sản phẩm',
  sheet: 'DMVTSP',
  cols: [
    { header: HEADERS.UUID, width: 38, get: row => row.uuid || '' },
    { header: HEADERS.MA_SAN_PHAM, width: 18, get: row => row.ma_vt || '' },
    { header: HEADERS.TEN_SAN_PHAM, width: 32, get: row => row.ten_vt || '' },
    { header: HEADERS.TEN_KHAC, width: 28, get: row => row.ten_vt2 || '' },
    { header: HEADERS.DON_VI_TINH, width: 14, get: row => row.dvt_data?.dvt || row.dvt_data?.ten_dvt || row.dvt || '' },
    {
      header: HEADERS.THEO_DOI_TON_KHO,
      width: 16,
      numFmt: CHECKBOX_NUMBER_FORMAT,
      get: row => boolToExcel(row.ton_kho_yn)
    },
    { header: HEADERS.THEO_DOI_LO, width: 14, numFmt: CHECKBOX_NUMBER_FORMAT, get: row => boolToExcel(row.lo_yn) },
    {
      header: HEADERS.THEO_DOI_TON_QUY_CACH,
      width: 18,
      numFmt: CHECKBOX_NUMBER_FORMAT,
      get: row => boolToExcel(row.qc_yn)
    },
    { header: HEADERS.LOAI_VAT_TU, width: 18, get: row => getCode(row, 'ma_lvt', 'ma_lvt_data', 'ma_lvt') },
    { header: HEADERS.NHOM_1, width: 18, get: row => getName(row, 'nh_vt1', 'nh_vt1_data', 'ten_phan_nhom') },
    { header: HEADERS.NHOM_2, width: 18, get: row => getName(row, 'nh_vt2', 'nh_vt2_data', 'ten_phan_nhom') },
    { header: HEADERS.NHOM_3, width: 18, get: row => getName(row, 'nh_vt3', 'nh_vt3_data', 'ten_phan_nhom') },
    { header: HEADERS.MA_KHO_MAC_DINH, width: 18, get: row => getCode(row, 'ma_kho', 'ma_kho_data', 'ma_kho') },
    {
      header: HEADERS.VI_TRI_MAC_DINH,
      width: 18,
      get: row => getCode(row, 'ma_vi_tri', 'ma_vi_tri_data', 'ma_vi_tri')
    },
    { header: HEADERS.MA_THUE_MAC_DINH, width: 18, get: row => getCode(row, 'ma_thue', 'ma_thue_data', 'ma_thue') },
    {
      header: HEADERS.MA_THUE_NHAP_KHAU,
      width: 18,
      get: row => getCode(row, 'ma_thue_nk', 'ma_thue_nk_data', 'ma_thue')
    },
    { header: HEADERS.TK_KHO_CHI_PHI, width: 18, get: row => getCode(row, 'tk_khcp', 'tk_khcp_data', 'code') },
    {
      header: HEADERS.SUA_TK_VAT_TU,
      width: 14,
      numFmt: CHECKBOX_NUMBER_FORMAT,
      get: row => boolToExcel(row.sua_tk_vt)
    },
    { header: HEADERS.TK_DOANH_THU, width: 18, get: row => getCode(row, 'tk_dt', 'tk_dt_data', 'code') },
    { header: HEADERS.TK_GIA_VON, width: 18, get: row => getCode(row, 'tk_gv', 'tk_gv_data', 'code') },
    { header: HEADERS.TK_CHIET_KHAU, width: 18, get: row => getCode(row, 'tk_ck', 'tk_ck_data', 'code') },
    { header: HEADERS.TK_KHUYEN_MAI, width: 18, get: row => getCode(row, 'tk_km', 'tk_km_data', 'code') },
    { header: HEADERS.TK_TRA_LAI, width: 18, get: row => getCode(row, 'tk_tl', 'tk_tl_data', 'code') },
    { header: HEADERS.TK_SAN_PHAM_DO_DANG, width: 18, get: row => getCode(row, 'tk_spdd', 'tk_spdd_data', 'code') },
    { header: HEADERS.TK_CHI_PHI_NVL, width: 18, get: row => getCode(row, 'tk_cpnvl', 'tk_cpnvl_data', 'code') },
    { header: HEADERS.MA_PHU, width: 18, get: row => row.ma_vt2 || '' },
    { header: HEADERS.MA_VACH, width: 18, get: row => row.ma_barcode || '' },
    { header: HEADERS.THE_TICH, width: 14, numFmt: DECIMAL_NUMBER_FORMAT, get: row => toExcelNumber(row.the_tich) },
    { header: HEADERS.KHOI_LUONG, width: 14, numFmt: DECIMAL_NUMBER_FORMAT, get: row => toExcelNumber(row.khoi_luong) },
    { header: HEADERS.NUOC_SAN_XUAT, width: 18, get: row => row.nuoc_sx || '' },
    { header: HEADERS.MAU_SAC, width: 18, get: row => getCode(row, 'ma_mau', 'ma_mau_data', 'ma_mau_sac') },
    { header: HEADERS.KICH_CO, width: 18, get: row => getCode(row, 'ma_kich_co', 'ma_kich_co_data', 'ma_kich_co') },
    { header: HEADERS.MA_BO_PHAN, width: 18, get: row => getCode(row, 'ma_bp', 'ma_bp_data', 'ma_bp') },
    { header: HEADERS.MA_PHI, width: 18, get: row => getCode(row, 'ma_phi', 'ma_phi_data', 'ma_phi') },
    { header: HEADERS.TON_TOI_THIEU, width: 14, numFmt: DECIMAL_NUMBER_FORMAT, get: row => toExcelNumber(row.sl_min) },
    { header: HEADERS.TON_TOI_DA, width: 14, numFmt: DECIMAL_NUMBER_FORMAT, get: row => toExcelNumber(row.sl_max) },
    { header: HEADERS.NGAY_NHAP_CUOI, width: 16, get: row => row.ngay_nhap || '' },
    { header: HEADERS.NGAY_XUAT_CUOI, width: 16, get: row => row.ngay_xuat || '' },
    { header: HEADERS.GHI_CHU, width: 28, get: row => row.ghi_chu || '' },
    { header: HEADERS.TRANG_THAI, width: 14, get: row => row.status ?? '' }
  ]
};

export const exportVatTuSanPhamExcel = (rows: VatTuSanPham[] = []) => save(rows, VAT_TU_SAN_PHAM_EXPORT_FILE);

export const fetchAllVatTuSanPhamForExport = async ({
  entitySlug,
  filters,
  pageSize = 500
}: {
  entitySlug: string;
  filters?: SearchValues;
  pageSize?: number;
}) => {
  const hasFilters = Boolean(filters && Object.keys(filters).length);

  return fetchAllForExport<VatTuSanPham>({
    entitySlug,
    endpoint: hasFilters ? `${QUERY_KEYS.VAT_TU}/filter/` : `${QUERY_KEYS.VAT_TU}/`,
    method: hasFilters ? 'POST' : 'GET',
    filters,
    pageSize
  });
};

export const exportVatTuSanPhamExcelFromApi = async ({
  entitySlug,
  filters
}: {
  entitySlug: string;
  filters?: SearchValues;
}) => {
  const hasFilters = Boolean(filters && Object.keys(filters).length);
  const rows = await exportFromApi<VatTuSanPham>({
    entitySlug,
    endpoint: hasFilters ? `${QUERY_KEYS.VAT_TU}/filter/` : `${QUERY_KEYS.VAT_TU}/`,
    method: hasFilters ? 'POST' : 'GET',
    filters,
    pageSize: 500,
    file: VAT_TU_SAN_PHAM_EXPORT_FILE,
    skipEmpty: true
  });

  return rows.length;
};

const toStringOrNull = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  const str = String(value).trim();
  return str === '' ? null : str;
};

const parseBoolean = (value: unknown, defaultValue = false): boolean => {
  if (value === undefined || value === null || String(value).trim() === '') {
    return defaultValue;
  }

  const normalized = String(value).toLowerCase().trim();
  const truthyValues = ['true', '1'];
  const falsyValues = ['false', '0'];

  if (truthyValues.includes(normalized)) return true;
  if (falsyValues.includes(normalized)) return false;

  return defaultValue;
};

const UUIDPayload = (parsedData: ParsedExcelData) => {
  const mainData = parsedData.main || {};
  const mainKeys = [
    'ma_vt',
    'dvt',
    'ma_lvt',
    'nh_vt1',
    'nh_vt2',
    'nh_vt3',
    'ma_kho',
    'ma_vi_tri',
    'ma_thue',
    'ma_thue_nk',
    'tk_khcp',
    'tk_dt',
    'tk_gv',
    'tk_ck',
    'tk_km',
    'tk_tl',
    'tk_dd',
    'tk_cp_nvl',
    'ma_bp',
    'ma_phi',
    'ma_mau',
    'ma_kich_co'
  ];

  const pick = (obj: Record<string, unknown[]>, keys: string[]) =>
    keys.reduce(
      (acc, key) => {
        if (key in obj) {
          acc[key] = obj[key];
        }
        return acc;
      },
      {} as Record<string, unknown[]>
    );

  return {
    main: pick(mainData, mainKeys)
  };
};

const getFieldName = (key: string) => {
  const config = HEADER_CONFIG.find(c => c.key === key);
  return config?.header?.replace('*', '').trim() || key;
};

type UUIDResponse = { main?: Record<string, Array<string | null | undefined>> };

const ValidateUUID = (response: unknown, columnMapping: Record<string, ColumnMapping>): ValidationError[] => {
  if (!response) return [];
  const uuidResponse = response as UUIDResponse;
  const errors: ValidationError[] = [];

  (['main'] as const).forEach(section => {
    const data = uuidResponse[section];
    if (!data) return;

    Object.entries(data).forEach(([field, values]) => {
      if (!Array.isArray(values)) return;
      const mapping = Object.values(columnMapping).find(m => m.key === field && m.type === section);

      values.forEach((val, i) => {
        // Material lookup fields are optional: null means this is a new material.
        if (field !== 'ma_vt' && field !== 'ma_vt_dvt' && val === null) {
          errors.push({
            row: i + 2,
            field,
            message: `${getFieldName(field)} không hợp lệ`,
            section,
            columnIndex: mapping?.index
          });
        }
      });
    });
  });

  return errors;
};

const TransformToBodies = (
  dataRows: unknown[][],
  parsedData: ParsedExcelData,
  UUIDResponse: UUIDResponse,
  context: { entityUnitUUID: string }
): ImportBody[] => {
  const mainData = parsedData.main || {};
  const rowCount = Math.max(0, ...Object.values(mainData).map(values => values.length));

  const bodies: ImportBody[] = [];

  for (let i = 0; i < rowCount; i++) {
    const body: ImportBody = {
      uuid:
        toStringOrNull(mainData.uuid?.[i] ?? '') || toStringOrNull(UUIDResponse.main?.ma_vt?.[i] ?? '') || undefined,
      ma_vt: toStringOrNull(mainData.ma_vt?.[i] ?? '') || '',
      ten_vt: toStringOrNull(mainData.ten_vt?.[i] ?? '') || '',
      ten_vt2: toStringOrNull(mainData.ten_vt2?.[i] ?? ''),
      dvt: (UUIDResponse.main?.dvt && UUIDResponse.main.dvt[i]) || toStringOrNull(mainData.dvt?.[i] ?? ''),

      ton_kho_yn: parseBoolean(mainData.ton_kho_yn?.[i], true),
      lo_yn: parseBoolean(mainData.lo_yn?.[i], false),
      qc_yn: parseBoolean(mainData.qc_yn?.[i], false),

      ma_lvt: (UUIDResponse.main?.ma_lvt && UUIDResponse.main.ma_lvt[i]) || toStringOrNull(mainData.ma_lvt?.[i] ?? ''),
      nh_vt1: (UUIDResponse.main?.nh_vt1 && UUIDResponse.main.nh_vt1[i]) || toStringOrNull(mainData.nh_vt1?.[i] ?? ''),
      nh_vt2: (UUIDResponse.main?.nh_vt2 && UUIDResponse.main.nh_vt2[i]) || toStringOrNull(mainData.nh_vt2?.[i] ?? ''),
      nh_vt3: (UUIDResponse.main?.nh_vt3 && UUIDResponse.main.nh_vt3[i]) || toStringOrNull(mainData.nh_vt3?.[i] ?? ''),

      ma_kho: (UUIDResponse.main?.ma_kho && UUIDResponse.main.ma_kho[i]) || toStringOrNull(mainData.ma_kho?.[i] ?? ''),
      ma_vi_tri:
        (UUIDResponse.main?.ma_vi_tri && UUIDResponse.main.ma_vi_tri[i]) ||
        toStringOrNull(mainData.ma_vi_tri?.[i] ?? ''),

      ma_thue:
        (UUIDResponse.main?.ma_thue && UUIDResponse.main.ma_thue[i]) || toStringOrNull(mainData.ma_thue?.[i] ?? ''),
      ma_thue_nk:
        (UUIDResponse.main?.ma_thue_nk && UUIDResponse.main.ma_thue_nk[i]) ||
        toStringOrNull(mainData.ma_thue_nk?.[i] ?? ''),

      tk_khcp:
        (UUIDResponse.main?.tk_khcp && UUIDResponse.main.tk_khcp[i]) || toStringOrNull(mainData.tk_khcp?.[i] ?? ''),
      sua_tk_vt: parseBoolean(mainData.sua_tk_vt?.[i], false),
      tk_dt: (UUIDResponse.main?.tk_dt && UUIDResponse.main.tk_dt[i]) || toStringOrNull(mainData.tk_dt?.[i] ?? ''),
      tk_gv: (UUIDResponse.main?.tk_gv && UUIDResponse.main.tk_gv[i]) || toStringOrNull(mainData.tk_gv?.[i] ?? ''),
      tk_ck: (UUIDResponse.main?.tk_ck && UUIDResponse.main.tk_ck[i]) || toStringOrNull(mainData.tk_ck?.[i] ?? ''),
      tk_km: (UUIDResponse.main?.tk_km && UUIDResponse.main.tk_km[i]) || toStringOrNull(mainData.tk_km?.[i] ?? ''),
      tk_tl: (UUIDResponse.main?.tk_tl && UUIDResponse.main.tk_tl[i]) || toStringOrNull(mainData.tk_tl?.[i] ?? ''),
      tk_spdd: (UUIDResponse.main?.tk_dd && UUIDResponse.main.tk_dd[i]) || toStringOrNull(mainData.tk_dd?.[i] ?? ''),
      tk_cpnvl:
        (UUIDResponse.main?.tk_cp_nvl && UUIDResponse.main.tk_cp_nvl[i]) ||
        toStringOrNull(mainData.tk_cp_nvl?.[i] ?? ''),

      ma_vt2: toStringOrNull(mainData.ma_vt2?.[i] ?? ''),
      ma_barcode: toStringOrNull(mainData.ma_barcode?.[i] ?? ''),
      the_tich: parseNumber(mainData.the_tich?.[i]),
      khoi_luong: parseNumber(mainData.khoi_luong?.[i]),
      nuoc_sx: toStringOrNull(mainData.nuoc_sx?.[i] ?? ''),
      ma_mau: (UUIDResponse.main?.ma_mau && UUIDResponse.main.ma_mau[i]) || toStringOrNull(mainData.ma_mau?.[i] ?? ''),
      ma_kich_co:
        (UUIDResponse.main?.ma_kich_co && UUIDResponse.main.ma_kich_co[i]) ||
        toStringOrNull(mainData.ma_kich_co?.[i] ?? ''),
      ma_bp: (UUIDResponse.main?.ma_bp && UUIDResponse.main.ma_bp[i]) || toStringOrNull(mainData.ma_bp?.[i] ?? ''),
      ma_phi: (UUIDResponse.main?.ma_phi && UUIDResponse.main.ma_phi[i]) || toStringOrNull(mainData.ma_phi?.[i] ?? ''),

      sl_min: parseNumber(mainData.sl_min?.[i], 0),
      sl_max: parseNumber(mainData.sl_max?.[i], 0),
      ngay_nhap: convertExcelDate(mainData.ngay_nhap?.[i]) || toStringOrNull(mainData.ngay_nhap?.[i] ?? ''),
      ngay_xuat: convertExcelDate(mainData.ngay_xuat?.[i]) || toStringOrNull(mainData.ngay_xuat?.[i] ?? ''),

      ghi_chu: toStringOrNull(mainData.ghi_chu?.[i] ?? ''),

      unit_id: context.entityUnitUUID,
      status: mainData.status?.[i] ?? 1,
      gia_ton: 0,
      kieu_hd: 1
    };

    bodies.push(body.uuid ? toPartialUpdateBody(body, mainData) : body);
  }

  return bodies;
};

export const ExcelConfig: ExcelProcessorConfig<ImportBody> = {
  Headers: HEADERS,
  HeaderConfig: HEADER_CONFIG,
  Transform: TransformToBodies,
  UUIDPayload,
  ValidateUUID
};
